<template>
  <div class="container mt-4" style="margin: auto;">
    <h2 class="text-center mb-4">网格详细信息<i class="fas fa-cube"></i></h2>

    <!-- 最新网格信息 -->
    <div class="latest-info-container" v-if="latestGrid">
      <div class="info-grid">
        <div class="info-tile" v-for="info in gridInfo" :key="info.label">
          <i :class="info.icon"></i>
          <span class="info-label">{{ info.label }}:</span>
          <span>{{ info.value }}</span>
        </div>
      </div>
    </div>

    <!-- Grid Chart -->
    <div class="mt-5">
      <h3 class="text-center">网格图表</h3>
      <v-chart class="chart" :option="pcuChartOptions" autoresize />
    </div>

    <!-- Historical List -->
    <div class="table-responsive mt-5">
      <h3 class="text-center">历史列表</h3>
      <table class="table table-dark table-striped">
        <thead>
          <tr>
            <th>时间</th>
            <th>星区</th>
            <th>网格 ID</th>
            <th>网格名称</th>
            <th>位置</th>
            <th>速度</th>
            <th>方块数</th>
            <th>PCU</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="grid in grids" :key="String(grid.GridID)">
            <td>{{ formatDate(grid.Time) }} ({{ timeAgo(grid.Time) }})</td>
            <td>{{ grid.ServerName }}</td>
            <td>{{ String(grid.GridID) }}</td>
            <td>{{ grid.GridName }}</td>
            <td>{{ formatPosition(grid.PositionX, grid.PositionY, grid.PositionZ) }}</td>
            <td>{{ calculateSpeed(grid.LinearVelocityX, grid.LinearVelocityY, grid.LinearVelocityZ) }} m/s</td>
            <td>{{ grid.BlockCount }}</td>
            <td>{{ grid.PCU }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from '../api/axios';
import { formatDate, timeAgo } from '../util/time';
import { ref, defineComponent } from 'vue-demi';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components';
import VChart, { THEME_KEY } from 'vue-echarts';

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
]);

export default defineComponent({
  name: 'GridDetail',
  components: {
    VChart,
  },
  provide: {
    [THEME_KEY]: 'dark',
  },
  props: {
    ownerid: {
      type: String,
      required: true,
    },
    gridname: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      grids: [],
      latestGrid: null,
      pcuChartOptions: {
        tooltip: {
          trigger: 'axis',
        },
        xAxis: {
          type: 'category',
          data: [],
        },
        yAxis: {
          type: 'value',
        },
        series: [
          {
            name: 'PCU',
            type: 'line',
            areaStyle: {},
            emphasis: {
              focus: 'series'
            },
            data: [],
          },
          {
            name: '格子数',
            type: 'line',
            areaStyle: {},
            emphasis: {
              focus: 'series'
            },
            data: [],
          },
        ],
      },
    };
  },
  watch: {
    ownerid: 'fetchGridDetails',
    gridname: 'fetchGridDetails'
  },
  methods: {
    fetchGridDetails() {
      axios.get(`/grid/${this.ownerid}/${this.gridname}`)
        .then(response => {
          this.grids = response.data;
          this.latestGrid = this.grids.length > 0 ? this.grids[0] : null;
          this.prepareChartData();
        })
        .catch(error => {
          console.error('获取网格详细信息时出错:', error);
        });
    },
    prepareChartData() {
      const labels = this.grids.map(grid => this.formatDate(grid.Time)).reverse();
      const pcuData = this.grids.map(grid => grid.PCU).reverse();
      const blockCountData = this.grids.map(grid => grid.BlockCount).reverse();

      this.pcuChartOptions.xAxis.data = labels;
      this.pcuChartOptions.series[0].data = pcuData;
      this.pcuChartOptions.series[1].data = blockCountData;
    },
    formatDate,
    timeAgo,
    formatPosition(x, y, z) {
      return `X: ${x.toFixed(2)}, Y: ${y.toFixed(2)}, Z: ${z.toFixed(2)}`;
    },
    calculateSpeed(x, y, z) {
      return Math.sqrt(x * x + y * y + z * z).toFixed(2);
    },
  },
  created() {
    this.fetchGridDetails();
  },
  computed: {
    gridInfo() {
      if (!this.latestGrid) return [];
      return [
        { label: '时间', value: this.formatDate(this.latestGrid.Time), icon: 'fas fa-clock' },
        { label: '星区', value: this.latestGrid.ServerName, icon: 'fas fa-globe-asia' },
        { label: '网格 ID', value: this.latestGrid.GridID, icon: 'fas fa-hashtag' },
        { label: '网格名称', value: this.latestGrid.GridName, icon: 'fas fa-cube' },
        { label: '位置', value: this.formatPosition(this.latestGrid.PositionX, this.latestGrid.PositionY, this.latestGrid.PositionZ), icon: 'fas fa-map-marker-alt' },
        { label: '速度', value: this.calculateSpeed(this.latestGrid.LinearVelocityX, this.latestGrid.LinearVelocityY, this.latestGrid.LinearVelocityZ) + ' m/s', icon: 'fas fa-tachometer-alt' },
        { label: '方块数', value: this.latestGrid.BlockCount, icon: 'fas fa-th' },
        { label: 'PCU', value: this.latestGrid.PCU, icon: 'fas fa-bolt' }
      ];
    }
  },
});
</script>

<style scoped>
.container {
  margin-top: 20px;
}
.table {
  margin-top: 20px;
}
.chart {
  height: 400px;
  width: 100%;
}

.latest-info-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #2c3e50;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.section-title {
  color: #fff;
  margin-bottom: 15px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  width: 100%;
}

.info-tile {
  background-color: #0003;
  border-radius: 5px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: #fff;
}

.info-label {
  font-weight: bold;
  margin-bottom: 5px;
}

.fas {
  margin-bottom: 5px;
}
</style>
